import { ReactElement, useState } from "react";

import { Header, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "semantic-ui-react";
import Case from "case";

import Property, { PropertyImage } from "../../rest/Property";
import Card from "../UI/Card/Card";
import EmblaCarousel from "../UI/Carousel/EmblaCarousel";

import "./PropertyImagesCard.scss";
import PropertyImageScoreChip from "./PropertyImageScoreChip";
import Button from "../UI/Button/Button";
import { useRestClientContext } from "../../contexts/RestClientContext";
import Preloader from "../UI/Preloader/Preloader";
import ConfirmationModal from "../Modal/ConfirmationModal";

interface PropertyImagesCardProps {
	property: Property;
}

export default function PropertyImagesCard({ property }: PropertyImagesCardProps) {
	const client = useRestClientContext();

	const [imageIndex, setImageIndex] = useState<number>(0);
	const [images, setImages] = useState<PropertyImage[] | undefined>(property.data.images);
	const [loading, setLoading] = useState<boolean>(false);
	const [isConfirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);

	const dataColumns: {
		label: string;
		column: keyof PropertyImage | ((propertyImage: PropertyImage) => ReactElement);
	}[] = [
		{ label: "Analysis", column: "result" },
		{ label: "Affected Areas", column: "affected_areas" },
		{
			label: "Performance Score",
			column: (propertyImage) => (
				<PropertyImageScoreChip
					score={propertyImage.insulation_performance}
					analysis={propertyImage.analysis}
				/>
			),
		},
		{ label: "Additional Comments", column: "additional_comments" },
	];

	// this format will probably be changed so that properties will only have one property image data set
	const propertyImage = property.data.images && property.data.images[0];

	const onDeleteImage = () => {
		if (!images) {
			return;
		}

		setLoading(true);
		// a bit of an antipattern, but don't wanna move embla state up
		const image = images[imageIndex];

		client.deletePropertyImage(property, image).then((_response: Response) => {
			setConfirmDeleteModalOpen(false);
			setImages(images.filter((i) => i !== image));
			setLoading(false);
		});
	};

	return (
		<>
			{images?.length && propertyImage ? (
				<Card title="Property Images" className="property-thermal-images">
					<div className="property-thermal-images-container">
						<div>
							<EmblaCarousel slides={images} setImageIndex={setImageIndex} />
						</div>
						<div className="table-container">
							<Table celled>
								<TableHeader>
									<TableRow>
										<TableHeaderCell>Analysis Column</TableHeaderCell>
										<TableHeaderCell>Value</TableHeaderCell>
									</TableRow>
								</TableHeader>
								<TableBody>
									{dataColumns.map((columnFormat) => (
										<TableRow key={columnFormat.label}>
											<TableCell width={8}>
												<Header>{Case.title(columnFormat.label)}</Header>
											</TableCell>
											{typeof columnFormat.column === "function" ? (
												<TableCell width={8}>{columnFormat.column(propertyImage)}</TableCell>
											) : (
												<TableCell width={8}>
													{propertyImage[columnFormat.column] ?? "N/A"}
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
							<Button onClick={() => setConfirmDeleteModalOpen(true)} variant="contained">
								Delete Image
							</Button>
						</div>
					</div>
				</Card>
			) : (
				<></>
			)}
			<ConfirmationModal
				title={"Are you sure you want to delete this image"}
				open={isConfirmDeleteModalOpen}
				buttonsDisabled={false}
				onConfirm={onDeleteImage}
				onCancel={() => setConfirmDeleteModalOpen(false)}
				confirmationText="Yes"
				cancellationText="No"
			>
				{isConfirmDeleteModalOpen && !loading ? (
					images?.length && <img src={images[imageIndex].url} alt="Selected thermal imagry to delete" />
				) : (
					<Preloader />
				)}
			</ConfirmationModal>
		</>
	);
}
